import { useState, useEffect } from 'react';
import { Search, Bell, Send, ArrowRight } from 'lucide-react';
import { collection, query, where, getDocs, orderBy as firestoreOrderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { Gap } from '../../types';
import { Tooltip } from '../shared';
import { Link } from 'react-router-dom';

/**
 * Fix List Screen - Prioritized action items to improve AI visibility
 */
export function GapList() {
  const { user } = useAuth();
  const [gaps, setGaps] = useState<Gap[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'quick-wins' | 'high-impact'>('all');

  useEffect(() => {
    loadGaps();
  }, [user]);

  const loadGaps = async () => {
    if (!user) return;

    try {
      const gapsQuery = query(
        collection(db, 'gaps'),
        where('userId', '==', user.uid),
        where('status', '==', 'open'),
        firestoreOrderBy('priorityScore', 'desc')
      );
      const snapshot = await getDocs(gapsQuery);
      const loadedGaps = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      })) as Gap[];
      setGaps(loadedGaps);
    } catch (error) {
      console.error('Error loading gaps:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate total potential score uplift
  const calculateScoreUplift = (gapsList: Gap[]) => {
    // Simplified: high impact = 8pts, medium = 4pts, low = 2pts
    return gapsList.reduce((total, gap) => {
      if (gap.impact === 'high') return total + 8;
      if (gap.impact === 'medium') return total + 4;
      return total + 2;
    }, 0);
  };

  const totalUplift = calculateScoreUplift(gaps);

  // Current score (would come from latest audit)
  const currentScore = 34; // This would be pulled from latest audit
  const projectedScore = currentScore + totalUplift;

  // Filter gaps
  const filteredGaps = gaps.filter((gap) => {
    if (selectedFilter === 'quick-wins') {
      return gap.effort === 'low';
    }
    if (selectedFilter === 'high-impact') {
      return gap.impact === 'high';
    }
    return true;
  });

  const getEffortColor = (effort: string) => {
    if (effort === 'low') return 'bg-blue-500';
    if (effort === 'medium') return 'bg-blue-500';
    return 'bg-blue-500';
  };

  const getEffortDots = (effort: string) => {
    if (effort === 'low') return 1;
    if (effort === 'medium') return 2;
    return 3;
  };

  const getEffortLabel = (effort: string) => {
    if (effort === 'low') return 'Low';
    if (effort === 'medium') return 'Med';
    return 'High';
  };

  const getImpactPoints = (impact: string) => {
    if (impact === 'high') return 8;
    if (impact === 'medium') return 4;
    return 2;
  };

  const getPrimaryTag = (gap: Gap) => {
    if (gap.impact === 'high') return { label: 'CRITICAL FIX', color: 'bg-red-100 text-red-700' };
    if (gap.impact === 'medium') return { label: 'HIGH IMPACT', color: 'bg-orange-100 text-orange-700' };
    if (gap.effort === 'low') return { label: 'QUICK WIN', color: 'bg-blue-100 text-blue-700' };
    return { label: 'FIX', color: 'bg-gray-100 text-gray-700' };
  };

  const getCategoryTag = (category: string) => {
    const categoryMap: Record<string, { label: string; color: string }> = {
      'pricing': { label: 'PRICING', color: 'bg-gray-100 text-gray-700' },
      'products': { label: 'PRODUCTS', color: 'bg-gray-100 text-gray-700' },
      'compliance': { label: 'COMPLIANCE', color: 'bg-gray-100 text-gray-700' },
      'missing-content': { label: 'CONTENT FIX', color: 'bg-gray-100 text-gray-700' },
      'outdated-content': { label: 'CONTENT FIX', color: 'bg-gray-100 text-gray-700' },
      'weak-schema': { label: 'VISIBILITY', color: 'bg-gray-100 text-gray-700' },
      'no-structured-data': { label: 'VISIBILITY', color: 'bg-gray-100 text-gray-700' },
      'competitor-dominance': { label: 'POSITIONING', color: 'bg-gray-100 text-gray-700' },
    };
    return categoryMap[category] || { label: category.toUpperCase().replace('-', ' '), color: 'bg-gray-100 text-gray-700' };
  };

  const getFixType = (gap: Gap) => {
    // Determine fix type based on suggested fix or category
    if (gap.suggestedFix?.toLowerCase().includes('content')) return { label: 'CONTENT FIX', color: 'bg-gray-100 text-gray-700' };
    if (gap.suggestedFix?.toLowerCase().includes('agent')) return { label: 'AGENT FIX', color: 'bg-gray-100 text-gray-700' };
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t1-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Audit</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Fix list</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search fixes..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t1-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">
            Growth
          </span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        {/* Page Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold text-t1-primary uppercase tracking-wide mb-1">Kodiac Audit</p>
            <h1 className="text-3xl font-semibold text-gray-900">Fix list</h1>
            <p className="text-sm text-gray-600 mt-1">
              {gaps.length} actions ranked by impact and effort — fixing these could add up to +{totalUplift} points
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
              <Send className="w-4 h-4" />
              Send to team
            </button>
            <Link
              to="/content-library"
              className="flex items-center gap-2 px-4 py-2 bg-t1-primary rounded-lg text-sm font-semibold text-white hover:bg-t1-dark"
            >
              Fix all with Kodiac Content
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Score Uplift Banner */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-6">
              <div>
                <p className="text-4xl font-bold text-green-600">+{totalUplift}</p>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  Estimated score uplift if all fixes are applied
                </h3>
                <p className="text-sm text-gray-600">
                  {totalUplift > 0 ? (
                    <>From {currentScore} to ~{projectedScore} · Brings you to industry average. Top 3 fixes alone add approximately +{Math.min(totalUplift, 14)} points.</>
                  ) : (
                    <Tooltip content="Run an audit scan to identify gaps and see potential score improvements">
                      <span className="text-gray-500">No open fixes · Run a scan to identify opportunities</span>
                    </Tooltip>
                  )}
                </p>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelectedFilter('all')}
                className={`px-3 py-1 text-sm font-medium rounded ${
                  selectedFilter === 'all'
                    ? 'bg-t1-primary text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                All ({gaps.length})
              </button>
              <button
                onClick={() => setSelectedFilter('quick-wins')}
                className={`px-3 py-1 text-sm font-medium rounded ${
                  selectedFilter === 'quick-wins'
                    ? 'bg-t1-primary text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Quick wins
              </button>
              <button
                onClick={() => setSelectedFilter('high-impact')}
                className={`px-3 py-1 text-sm font-medium rounded ${
                  selectedFilter === 'high-impact'
                    ? 'bg-t1-primary text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                High impact
              </button>
            </div>
          </div>
        </div>

        {/* Prioritised Fix List */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-base font-semibold text-gray-900">Prioritised fix list</h2>
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <span>Effort:</span>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span>Low</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span>Med</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span>High</span>
              </div>
            </div>
          </div>

          {filteredGaps.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {selectedFilter !== 'all'
                  ? `No ${selectedFilter === 'quick-wins' ? 'quick win' : 'high impact'} fixes found.`
                  : 'No open fixes. Run an audit to identify opportunities.'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredGaps.map((gap, index) => {
                const primaryTag = getPrimaryTag(gap);
                const categoryTag = getCategoryTag(gap.category);
                const fixType = getFixType(gap);
                const points = getImpactPoints(gap.impact);

                return (
                  <div
                    key={gap.id}
                    className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {/* Number */}
                    <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                      <span className="text-gray-400 font-medium">{index + 1}</span>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-900 mb-2">
                        {gap.description}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">
                        {gap.suggestedFix}
                      </p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${primaryTag.color}`}>
                          {primaryTag.label}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${categoryTag.color}`}>
                          {categoryTag.label}
                        </span>
                        {fixType && (
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${fixType.color}`}>
                            {fixType.label}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Effort & Points */}
                    <div className="flex items-center gap-6 flex-shrink-0">
                      {/* Effort Indicator */}
                      <div className="text-center">
                        <div className="flex items-center gap-0.5 mb-1">
                          {[...Array(3)].map((_, i) => (
                            <div
                              key={i}
                              className={`w-2 h-2 rounded-full ${
                                i < getEffortDots(gap.effort) ? getEffortColor(gap.effort) : 'bg-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <p className="text-xs text-gray-500">
                          <span className="uppercase">{getEffortLabel(gap.effort)}</span>
                        </p>
                      </div>

                      {/* Points */}
                      <div className="text-right">
                        <p className="text-xl font-bold text-green-600">+{points} pts</p>
                      </div>

                      {/* Fix Button */}
                      <Link
                        to={`/gaps/${gap.id}`}
                        className="px-4 py-2 bg-t1-primary text-white rounded-lg text-sm font-medium hover:bg-t1-dark flex items-center gap-1"
                      >
                        Fix now
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
